'use client';

import { Box, Pagination } from '@wishket/design-system';
import { useEffect } from 'react';

import AuthorListItem from './AuthorListItem';

import { useDeviceCheck, useQueryParams } from '@/shared/hooks';
import { useAuthorListQuery } from '@/entities/author';
import { AuthorListCardProps } from '@/shared/types';
import { FadeUpContainer } from '@/shared/Components';
import { AuthorListSkeleton } from '@/features/Author';

const DEFAULT_PAGE = 1;

const AuthorList = () => {
  const { isDesktop } = useDeviceCheck();
  const [searchParams, setSearchParams, validate] = useQueryParams<{
    page: number;
  }>({
    defaultValues: {
      page: DEFAULT_PAGE,
    },
  });

  const { page } = searchParams;
  const { isPending, data } = useAuthorListQuery({ page });

  const changePage = (pageNumber: number) => {
    if (typeof window === 'undefined') return;
    setSearchParams({ page: pageNumber });

    window.scrollTo({ top: 0 });
  };

  useEffect(() => {
    const validPage = validate.number('page', String(page));

    setSearchParams({ page: validPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  if (isPending || !data) return <AuthorListSkeleton />;

  return (
    <Box className="flex w-full flex-col gap-12 desktop:gap-20">
      <FadeUpContainer delay={0.1} className="flex w-full flex-col gap-6">
        {data?.authors.map((author: AuthorListCardProps) => (
          <AuthorListItem
            key={author.id}
            id={author.id}
            name={author.name}
            imageUrl={author.imageUrl}
            count={author.count}
            urlIdentifier={author.urlIdentifier}
            mostPublishedCategory={author.mostPublishedCategory}
            description={author.description}
            tagLine={author.tagLine}
            isSubscribed={author.isSubscribed}
            isCompany={author.isCompany}
            page={page}
          />
        ))}
      </FadeUpContainer>

      <Pagination
        maxPage={data.maxPage}
        page={Number(page)}
        onChange={changePage}
        pageCount={isDesktop ? 10 : 5}
      />
    </Box>
  );
};

export default AuthorList;
